// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * A serial's state, in a few words under the input that holds it (design doc §7.5): being
 * checked, never filed, installed on another machine (which blocks submitting), or used
 * before and since taken off or scrapped (a warning — it may go back on). On a change
 * screen a serial still installed here but released by this very change is a swap, not a
 * conflict.
 */

import { useTranslation } from "react-i18next";
import type { SerialState } from "@/scanner/useSerialStatus";

export type NoteState = SerialState | { kind: "swap"; position: string } | { kind: "duplicate" };

export function SerialStatusNote({ state }: { state: NoteState | null }) {
  const { t } = useTranslation();
  if (!state) return null;
  switch (state.kind) {
    case "checking":
      return <span className="serial-note muted">{t("serialStatus.checking")}</span>;
    case "free":
      return <span className="serial-note is-free">{t("serialStatus.free")}</span>;
    case "installed":
      return (
        <span className="serial-note is-installed">
          {t("archive.installedShort", {
            archiveNo: state.hit.archive_no,
            position: state.hit.position_name,
          })}
        </span>
      );
    case "used":
      return (
        <span
          className={
            state.hit.scrap_outcome === "SCRAPPED"
              ? "serial-note is-installed"
              : "serial-note is-used"
          }
        >
          {t(
            state.hit.archive_status !== "SCRAPPED"
              ? "serialStatus.usedRemoved"
              : state.hit.scrap_outcome === "SCRAPPED"
                ? "serialStatus.usedPartScrapped"
                : state.hit.scrap_outcome === "RESTOCKED"
                  ? "serialStatus.usedPartRestocked"
                  : "serialStatus.usedScrapped",
            {
              archiveNo: state.hit.archive_no,
              position: state.hit.position_name,
              version: state.hit.version_no,
            },
          )}
        </span>
      );
    case "swap":
      return (
        <span className="serial-note is-free">
          {t("serialStatus.swap", { position: state.position })}
        </span>
      );
    case "duplicate":
      return <span className="serial-note is-installed">{t("serialStatus.duplicate")}</span>;
  }
}

/** A serial its material's barcode rules reject, and the formats they accept. */
export function BarcodeMismatchNote({
  materialCode,
  patterns,
}: {
  materialCode: string | null | undefined;
  patterns: string[];
}) {
  const { t } = useTranslation();
  return (
    <span className="serial-note is-installed">
      {t("serialStatus.formatMismatch", {
        material: materialCode ?? "",
        patterns: patterns.join(" / "),
      })}
    </span>
  );
}
